import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, CircularProgress,
  TextField, MenuItem, Grid, Pagination
} from '@mui/material';
import {
  TrendingUp, TrendingDown, AccountBalanceWallet
} from '@mui/icons-material';
import { api } from '../context/AuthContext';

const CreditLedgerPage = () => {
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const [pagination, setPagination] = useState({
    totalRecords: 0,
    totalPages: 1,
    limit: 10
  });
  const [filters, setFilters] = useState({
    type: ''
  });

  useEffect(() => {
    fetchLedger();
  }, [filters, page]);

  const fetchLedger = async () => {
    setLoading(true);

    try {
      let url = `/applications/comm-center/ledger?page=${page}&limit=10`;

      if (filters.type) {
        url += `&type=${filters.type}`;
      }

      const res = await api.get(url);

      setLedger(res.data.data || []);

      setPagination({
        totalRecords: res.data.pagination?.totalRecords || 0,
        totalPages: res.data.pagination?.totalPages || 1,
        limit: res.data.pagination?.limit || 10
      });

    } catch (err) {
      console.error('Failed to fetch ledger:', err);
    } finally {
      setLoading(false);
    }
  };
  const startRecord =
      pagination.totalRecords === 0
          ? 0
          : (page - 1) * pagination.limit + 1;

  const endRecord = Math.min(
      page * pagination.limit,
      pagination.totalRecords
  );

  return (
    <Box sx={{ p: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ color: "#1E3A8A", fontWeight: "bold", fontSize: { xs: '1.5rem', md: '2rem' } }}>
                  Credit Ledger
                </Typography>
        <Typography variant="body1" color="text.secondary">Audit trail of all credit purchases and usage</Typography>
      </Box>

      <Card sx={{ mb: 4, borderRadius: '16px' }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Credit Type"
                value={filters.type}
                onChange={(e) => {
                  setPage(1);
                  setFilters({
                    ...filters,
                    type: e.target.value
                  });
                }}
                size="small"
              >
                <MenuItem value="">All Credits</MenuItem>
                <MenuItem value="calling">Calling Credits</MenuItem>
                <MenuItem value="whatsapp">WhatsApp Credits</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <TableContainer component={Paper} sx={{ borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        <Table>
          <TableHead sx={{ bgcolor: '#F8FAFC' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Transaction Type</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Credit Type</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Added</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Used</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Balance</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
                <TableRow><TableCell colSpan={7} align="center"><CircularProgress size={24} sx={{ my: 2 }} /></TableCell></TableRow>
            ) : ledger.length > 0 ? ledger.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                    <Typography variant="body2">{new Date(row.created_at).toLocaleDateString()}</Typography>
                    <Typography variant="caption" color="text.secondary">{new Date(row.created_at).toLocaleTimeString()}</Typography>
                </TableCell>
                <TableCell>
                    <Chip 
                        icon={row.transaction_type === 'purchase' ? <TrendingUp /> : <TrendingDown />}
                        label={row.transaction_type}
                        size="small"
                        color={row.transaction_type === 'purchase' ? 'success' : 'warning'}
                        variant="outlined"
                        sx={{ fontWeight: 600, textTransform: 'capitalize' }}
                    />
                </TableCell>
                <TableCell>
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{row.type}</Typography>
                </TableCell>
                <TableCell>{row.description}</TableCell>
                <TableCell align="right" sx={{ color: 'success.main', fontWeight: 700 }}>
                    {row.credits_added > 0 ? `+${row.credits_added}` : '-'}
                </TableCell>
                <TableCell align="right" sx={{ color: 'error.main', fontWeight: 700 }}>
                    {row.credits_used > 0 ? `-${row.credits_used}` : '-'}
                </TableCell>
                <TableCell align="right">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                        <AccountBalanceWallet sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" fontWeight={700}>{row.balance}</Typography>
                    </Box>
                </TableCell>
              </TableRow>
            )) : (
                <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No ledger entries found</Typography>
                    </TableCell>
                </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 2
          }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {startRecord} to {endRecord} of {pagination.totalRecords} entries
        </Typography>

        <Pagination
            page={page}
            count={pagination.totalPages}
            onChange={(event, value) => setPage(value)}
            color="primary"
            shape="rounded"
        />
      </Box>
    </Box>
  );
};

export default CreditLedgerPage;
